import { useState, useEffect, useRef } from "react";
import styles from "./linzeSalesReport.module.scss";
import PageCard from "../../../components/PageCard";
import ButtonShared from "../../../components/Button/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import moment, { Moment } from "moment";
import { LinzeSalesReportPageProps } from "../../../interfaces/pages";

const POLLING_INTERVAL_MS = 4000;

const LinzeSalesReport = ({
    linzeSalesReport,
    requestLinzeSalesReport,
    checkLinzeSalesReport,
    cleanLinzeSalesReport
}: LinzeSalesReportPageProps) => {
    const [dateFrom, setDateFrom] = useState<Moment | null>(moment().subtract(7, 'days'));
    const [dateTo, setDateTo] = useState<Moment | null>(moment());
    const [deletedFilter, setDeletedFilter] = useState<string>("all");
    const [downloading, setDownloading] = useState<boolean>(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (linzeSalesReport.fetching === "polling" && linzeSalesReport.jobId) {
            intervalRef.current = setInterval(() => {
                checkLinzeSalesReport(linzeSalesReport.jobId!);
            }, POLLING_INTERVAL_MS);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [linzeSalesReport.fetching, linzeSalesReport.jobId]);

    const handleGenerateReport = () => {
        cleanLinzeSalesReport();
        requestLinzeSalesReport(
            dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
            dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
            deletedFilter === "all" ? undefined : deletedFilter
        );
    };

    const isLoading = linzeSalesReport.fetching === "requesting" || linzeSalesReport.fetching === "polling";

    const handleDownload = () => {
        const url = linzeSalesReport.fileUrl;
        if (!url) return;
        setDownloading(true);
        fetch(url)
            .then(res => {
                if (!res.ok) throw new Error("download failed");
                return res.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = linzeSalesReport.fileName || "reporte.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(() => {
                // Fallback si el bucket no habilita CORS para fetch: navegación directa
                window.open(url, "_blank");
            })
            .finally(() => setDownloading(false));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <PageCard className={styles.reportsContainer}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Ventas Linze</h2>
                    <p className={styles.subtitle}>Genera un reporte de publicaciones y su estado de venta a través del flujo Linze</p>
                </div>

                <div className={styles.reportCard}>
                    <h3 className={styles.cardTitle}>Reporte de publicaciones</h3>
                    <p className={styles.cardDescription}>
                        Por defecto se filtra por la última semana. El reporte tiene un tope de 100.000 registros por corrida.
                    </p>

                    <div className={styles.dateRange}>
                        <DatePicker
                            label="Fecha desde"
                            value={dateFrom}
                            onChange={(newValue) => setDateFrom(newValue)}
                            renderInput={(params) => <TextField {...params} />}
                            maxDate={moment()}
                            inputFormat="DD/MM/YYYY"
                        />
                        <DatePicker
                            label="Fecha hasta"
                            value={dateTo}
                            onChange={(newValue) => setDateTo(newValue)}
                            renderInput={(params) => <TextField {...params} />}
                            maxDate={moment()}
                            minDate={dateFrom}
                            inputFormat="DD/MM/YYYY"
                        />
                        <FormControl fullWidth>
                            <InputLabel sx={{ '&.Mui-focused': { color: '#1A1C21' } }}>Eliminadas</InputLabel>
                            <Select
                                value={deletedFilter}
                                label="Eliminadas"
                                onChange={(e) => setDeletedFilter(e.target.value)}
                            >
                                <MenuItem value="all">Todas</MenuItem>
                                <MenuItem value="true">Solo eliminadas</MenuItem>
                                <MenuItem value="false">Solo no eliminadas</MenuItem>
                            </Select>
                        </FormControl>
                        <ButtonShared
                            className={styles.generateButton}
                            title={isLoading ? "Generando reporte..." : "Generar Reporte"}
                            onPress={handleGenerateReport}
                            disabled={!dateFrom || !dateTo || isLoading}
                            icon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                            primary={true}
                            fontSize="small"
                        />
                    </div>

                    {linzeSalesReport.fetching === "done" && linzeSalesReport.fileUrl && (
                        <div className={styles.resultCard}>
                            <p>Reporte generado con {linzeSalesReport.totalRecords} registros.</p>
                            <button
                                type="button"
                                className={styles.downloadLink}
                                onClick={handleDownload}
                                disabled={downloading}
                            >
                                {downloading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon fontSize="small" />}
                                {downloading ? "Descargando..." : `Descargar ${linzeSalesReport.fileName}`}
                            </button>
                        </div>
                    )}

                    {linzeSalesReport.fetching === "error" && (
                        <div className={styles.errorCard}>
                            <p>{linzeSalesReport.error || "Ha ocurrido un error al generar el reporte."}</p>
                        </div>
                    )}
                </div>
            </PageCard>
        </LocalizationProvider>
    );
};

export default LinzeSalesReport;
